import type { Database } from '../../database.types';
import { compressIfNeeded } from '../../storage/compress-image';
import { normalizeMimeType } from '../../storage/image-rules';
import { buildPhotoStoragePath } from '../../storage/photo-paths';
import { readImageDimensions } from '../../storage/read-image-dimensions';
import { getSupabaseClient, type DdmSupabaseClient } from '../../supabase';

export type DealPhoto = Database['public']['Tables']['deal_photos']['Row'];
export type DealPhotoInsert = Database['public']['Tables']['deal_photos']['Insert'];

export const DEAL_PHOTOS_BUCKET = 'deal-photos';
/** 1-year cache: ADR-012 §C5. Path is immutable so this is safe. */
export const CACHE_CONTROL_ONE_YEAR = '31536000';

/**
 * Fetch the photos of a deal, ordered by `position` ascending so position
 * 0 is always the cover. Returns an empty array when the deal has none
 * (rather than null) so consumers can render a uniform empty-state.
 *
 * RLS: `deal_photos_select` (003_deals.sql §14.3) — anon/buyer see
 * photos of published/sold deals only, admins/super_admins see all.
 */
export async function listDealPhotos(
  dealId: string,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<DealPhoto[]> {
  const { data, error } = await client
    .from('deal_photos')
    .select('*')
    .eq('deal_id', dealId)
    .order('position', { ascending: true });
  if (error) throw error;
  return (data ?? []) as DealPhoto[];
}

export type UploadDealPhotoOptions = {
  altText?: string | null;
};

/**
 * BO-3 upload pipeline:
 *   1. Compress source if `> 5 MB` (browser-image-compression, target 2 MB).
 *   2. Build an immutable Storage path `deals/{dealId}/{uuid}.{ext}`.
 *   3. Upload binary with `cacheControl=31536000` and `upsert=false`.
 *   4. Compute the next free `position` (0 if first photo, otherwise
 *      `max(position) + 1`).
 *   5. INSERT the row in `deal_photos`. The
 *      `deal_photos_enforce_max_count_trg` trigger surfaces the 16th
 *      attempt with a clear `check_violation`.
 *
 * If step 5 fails (RLS, max-count, or any constraint) the just-uploaded
 * blob is removed best-effort to keep Storage and DB aligned. A
 * residual orphan is at worst quota nibble — V2 cron sweep
 * (ADR-012 §C5) covers the long tail.
 */
export async function uploadDealPhoto(
  dealId: string,
  file: File,
  options: UploadDealPhotoOptions = {},
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<DealPhoto> {
  const compressed = await compressIfNeeded(file);
  // Read dimensions of the *compressed* file — browser-image-compression may
  // have downscaled to fit the size budget, and we want the persisted values
  // to match the asset actually served. Best-effort: a decode failure (rare —
  // a re-encoded JPEG always decodes) falls back to NULL dims rather than
  // failing the upload at this late stage.
  const dimensions = await readImageDimensions(compressed).catch(() => null);
  const { path } = buildPhotoStoragePath(dealId, compressed);

  const { error: uploadError } = await client.storage
    .from(DEAL_PHOTOS_BUCKET)
    .upload(path, compressed, {
      cacheControl: CACHE_CONTROL_ONE_YEAR,
      upsert: false,
      contentType: compressed.type,
    });
  if (uploadError) throw uploadError;

  const {
    data: { publicUrl },
  } = client.storage.from(DEAL_PHOTOS_BUCKET).getPublicUrl(path);

  const { data: existing, error: existingError } = await client
    .from('deal_photos')
    .select('position')
    .eq('deal_id', dealId)
    .order('position', { ascending: false })
    .limit(1);
  if (existingError) {
    await removeStorageObject(client, path);
    throw existingError;
  }

  const nextPosition = existing && existing.length > 0 ? (existing[0]?.position ?? -1) + 1 : 0;

  const insertPayload: DealPhotoInsert = {
    deal_id: dealId,
    url: publicUrl,
    storage_key: path,
    position: nextPosition,
    size_bytes: compressed.size,
    width_px: dimensions?.width ?? null,
    height_px: dimensions?.height ?? null,
    mime_type: normalizeMimeType(compressed.type),
    alt_text: options.altText ?? null,
  };

  const { data: inserted, error: insertError } = await client
    .from('deal_photos')
    .insert(insertPayload)
    .select('*')
    .single();

  if (insertError) {
    await removeStorageObject(client, path);
    throw insertError;
  }

  return inserted as DealPhoto;
}

/**
 * Delete a deal photo. The DB row is removed first — if RLS rejects, no
 * orphan blob is left behind. Storage cleanup is best-effort (logged on
 * failure, never thrown) because a stale blob is not user-visible.
 */
export async function deleteDealPhoto(
  photo: Pick<DealPhoto, 'id' | 'storage_key'>,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<void> {
  const { error: deleteError } = await client.from('deal_photos').delete().eq('id', photo.id);
  if (deleteError) throw deleteError;

  await removeStorageObject(client, photo.storage_key);
}

/**
 * Bulk-delete several deal photos via parallel {@link deleteDealPhoto}
 * calls. Uses `Promise.allSettled` so one RLS / storage hiccup does not
 * sink the rest of the batch — the caller gets a `{ ok, failed }` tally
 * and can toast the partial outcome.
 */
export async function deleteDealPhotos(
  photos: ReadonlyArray<Pick<DealPhoto, 'id' | 'storage_key'>>,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<{ ok: number; failed: number }> {
  const results = await Promise.allSettled(photos.map((p) => deleteDealPhoto(p, client)));
  const ok = results.filter((r) => r.status === 'fulfilled').length;
  return { ok, failed: results.length - ok };
}

/**
 * Atomic reorder via the `reorder_deal_photos` RPC (SECURITY INVOKER).
 * The list MUST contain every photo of the deal exactly once; partial
 * lists raise `invalid_parameter_value` (22023).
 */
export async function reorderDealPhotos(
  dealId: string,
  orderedIds: string[],
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<void> {
  const { error } = await client.rpc('reorder_deal_photos', {
    p_deal_id: dealId,
    p_ordered_photo_ids: orderedIds,
  });
  if (error) throw error;
}

/**
 * Convenience wrapper around {@link reorderDealPhotos} that pins a
 * specific photo at position 0 while preserving the relative order of
 * the rest. Pass the current `orderedIds` from the gallery state.
 */
export async function setCoverDealPhoto(
  dealId: string,
  photoId: string,
  currentOrderedIds: string[],
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<void> {
  if (!currentOrderedIds.includes(photoId)) {
    throw new Error(`setCoverDealPhoto: photo ${photoId} is not in the current order`);
  }
  const without = currentOrderedIds.filter((id) => id !== photoId);
  return reorderDealPhotos(dealId, [photoId, ...without], client);
}

async function removeStorageObject(client: DdmSupabaseClient, path: string): Promise<void> {
  try {
    await client.storage.from(DEAL_PHOTOS_BUCKET).remove([path]);
  } catch (err) {
    console.warn(`removeStorageObject: failed to remove "${path}" from Storage`, err);
  }
}

export type RetryDealPhotoCopyResult = {
  success: true;
  /** Final `deals.photos_copy_status` after retry — drives the UI toast. */
  photos_copy_status: 'ok' | 'failed' | 'none';
  /** Present only when status='failed' — short reason string from the Edge. */
  photo_error?: string;
};

type RetryDealPhotoCopyErrorBody = {
  success: false;
  code: string;
};

/**
 * Retry the seller-photos → deal-photos copy for a deal whose
 * `photos_copy_status` landed in 'failed' (Lot 4 audit V2 §7.3).
 *
 * Wraps the `retry-deal-photo-copy` Edge Function (verify_jwt=true).
 * The Edge:
 *   - validates the caller is admin/super_admin (via the user JWT),
 *   - finds the originating submission via
 *     `seller_submissions.converted_deal_id = dealId`,
 *   - cleans any partial deal_photos from a previous failure,
 *   - re-runs the copy pipeline from `accept-seller-submission`,
 *   - flips `deals.photos_copy_status` to 'ok' / 'failed' / 'none'.
 *
 * The deal must currently be in `photos_copy_status='failed'`. Any
 * other state (ok / none / pending / copying) returns 409
 * `not_retryable` and we surface that to the caller as a thrown error
 * with `code='not_retryable'`.
 */
export async function retryDealPhotoCopy(
  dealId: string,
  client: DdmSupabaseClient = getSupabaseClient(),
): Promise<RetryDealPhotoCopyResult> {
  const { data, error } = await client.functions.invoke('retry-deal-photo-copy', {
    body: { deal_id: dealId },
  });
  if (error) throw error;
  const body = data as RetryDealPhotoCopyResult | RetryDealPhotoCopyErrorBody;
  if (body.success === false) {
    const err = new Error(`retry-deal-photo-copy: ${body.code}`);
    (err as Error & { code?: string }).code = body.code;
    throw err;
  }
  return body;
}
